/**
 * The Leaflet canvas every ODL map surface renders: sites as status-coloured
 * markers, clustered client-side with a conic-gradient ring reporting what is
 * beneath each cluster, a hover card, and a gold day/night terminator so an
 * operator can tell at a glance which sites should currently be lit.
 *
 * The site list is entirely the caller's (fetch, filter, search, sort) — this
 * is a controlled, imperative renderer. It re-lays markers when `sites`
 * changes, fits whenever `fitKey` changes, mirrors the caller's hovered and
 * selected rows, reports viewport moves back so the caller can put them in the
 * URL, and exposes `findOnMap(siteId)` for a sidebar's locate action. Clicking
 * a marker only selects — navigation is the caller's decision.
 *
 * A caller whose own page is scoped to a time window passes it as
 * `popoverHours`, which narrows the hover card to alarms inside that window.
 */
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import L from 'leaflet';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MapMarkerPopover from '@sophocles/components/map/MapMarkerPopover';
import {
  createBreakdownClusterIcon,
  createSelectedSiteIcon,
  createSiteIcon,
  siteMarkerColor,
} from '@sophocles/components/map/siteMapIcons';
import { createDayNightTerminatorLayer } from '@sophocles/features/dashboards/components/dayNightTerminatorLayer';
import { deriveSiteStatus } from '@sophocles/features/map/mapState';

require('leaflet.markercluster');

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const DEFAULT_CENTER = [37.0, -96.0];
const DEFAULT_ZOOM = 4;
const MIN_ZOOM = 2;
const FIND_ON_MAP_ZOOM = 15;
const FIT_MAX_ZOOM = 14;
const FIT_PADDING = [40, 40];
const FLY_DURATION_SECONDS = 0.5;
const HIGHLIGHT_Z_OFFSET = 1000;
const SELECTED_Z_OFFSET = 2000;
const INVALIDATE_SIZE_DELAY_MS = 200;
// A drag fires moveend once, but an inertial fling and a zoom animation each
// settle in bursts; coalescing keeps one pan from rewriting the URL repeatedly.
const BOUNDS_CHANGE_DEBOUNCE_MS = 300;

const CLUSTER_OPTIONS = {
  maxClusterRadius: 50,
  disableClusteringAtZoom: FIND_ON_MAP_ZOOM,
  spiderfyOnMaxZoom: true,
  showCoverageOnHover: false,
  chunkedLoading: true,
  iconCreateFunction: createBreakdownClusterIcon,
};

function hasPosition(site) {
  return site.latitude != null && site.longitude != null;
}

function toBoundsPayload(bounds) {
  return {
    south: bounds.getSouth(),
    west: bounds.getWest(),
    north: bounds.getNorth(),
    east: bounds.getEast(),
  };
}

function SiteMap(props, ref) {
  const {
    sites,
    isBillboard = false,
    hoveredSiteId = null,
    selectedSiteId = null,
    onHoverSite,
    onSelectSite,
    fitKey,
    initialBounds = null,
    onBoundsChange,
    showTerminator = true,
    popoverHours = null,
  } = props;

  const { t } = useTranslation();
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const clusterGroupRef = useRef(null);
  const dayNightLayerRef = useRef(null);
  const markersByIdRef = useRef(new Map());

  const [hoveredSite, setHoveredSite] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(null);

  // Latest handlers/data without retriggering the marker-lay effect on identity.
  const handlersRef = useRef({ onHoverSite, onSelectSite, onBoundsChange });
  handlersRef.current = { onHoverSite, onSelectSite, onBoundsChange };

  // The bbox the map opened on is a starting position, not a controlled value —
  // re-reading it on every URL write would fight the user's own panning.
  const initialBoundsRef = useRef(initialBounds);
  // An opening viewport outranks only the *first* fit. Every later one is the
  // caller deliberately re-framing the map (a new group, a fresh dataset) and
  // must still move it, or a shared link would freeze the map forever.
  const openingFitPendingRef = useRef(Boolean(initialBounds));

  useImperativeHandle(
    ref,
    () => ({
      findOnMap(siteId) {
        const map = mapRef.current;
        const marker = markersByIdRef.current.get(siteId);
        if (!map || !marker) return;
        map.flyTo(marker.getLatLng(), Math.max(map.getZoom(), FIND_ON_MAP_ZOOM), { duration: FLY_DURATION_SECONDS });
      },
    }),
    [],
  );

  // Init the map once.
  useEffect(() => {
    if (!mapElRef.current || mapRef.current) return undefined;

    const map = L.map(mapElRef.current, {
      scrollWheelZoom: true,
      // The filter bar sits above the canvas rather than over its top-left
      // corner, but the sidebar's edge is the quieter side for the control.
      zoomControl: false,
      attributionControl: false,
      boxZoom: true,
      minZoom: MIN_ZOOM,
      worldCopyJump: true,
    }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);

    L.control.zoom({ position: 'topright' }).addTo(map);

    const opening = initialBoundsRef.current;
    if (opening) {
      map.fitBounds(L.latLngBounds([opening.south, opening.west], [opening.north, opening.east]), { animate: false });
    }

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '', maxZoom: 19 }).addTo(map);

    const clusterGroup = L.markerClusterGroup(CLUSTER_OPTIONS);
    map.addLayer(clusterGroup);
    clusterGroupRef.current = clusterGroup;
    mapRef.current = map;

    let boundsTimer = null;
    const handleMoveEnd = () => {
      clearTimeout(boundsTimer);
      boundsTimer = setTimeout(
        () => handlersRef.current.onBoundsChange?.(toBoundsPayload(map.getBounds())),
        BOUNDS_CHANGE_DEBOUNCE_MS,
      );
    };
    map.on('moveend', handleMoveEnd);

    const invalidateTimer = setTimeout(() => map.invalidateSize(), INVALIDATE_SIZE_DELAY_MS);

    return () => {
      clearTimeout(invalidateTimer);
      clearTimeout(boundsTimer);
      map.off('moveend', handleMoveEnd);
      if (dayNightLayerRef.current) map.removeLayer(dayNightLayerRef.current);
      // A flyTo/flyToBounds animation frame that survives remove() touches
      // torn-down panes (the classic _leaflet_pos crash) — stop it first.
      map.stop();
      map.remove();
      mapRef.current = null;
      clusterGroupRef.current = null;
      dayNightLayerRef.current = null;
      markersByIdRef.current = new Map();
    };
  }, []);

  // The terminator is a toggle, so it attaches and detaches on its own.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    if (!showTerminator) return undefined;
    const layer = createDayNightTerminatorLayer().addTo(map);
    dayNightLayerRef.current = layer;
    return () => {
      map.removeLayer(layer);
      dayNightLayerRef.current = null;
    };
  }, [showTerminator]);

  // Re-lay markers whenever the visible site set or its colouring changes.
  useEffect(() => {
    const clusterGroup = clusterGroupRef.current;
    if (!clusterGroup) return;

    clusterGroup.clearLayers();
    const byId = new Map();
    sites.filter(hasPosition).forEach(site => {
      const status = deriveSiteStatus(site);
      const color = siteMarkerColor(site);
      const marker = L.marker([site.latitude, site.longitude], {
        icon: createSiteIcon(site, color),
        _status: status,
        _color: color,
      });
      marker.on('mouseover', event => {
        setHoveredSite(site);
        setHoverPosition({ x: event.containerPoint.x, y: event.containerPoint.y });
        handlersRef.current.onHoverSite?.(site.id);
      });
      marker.on('mouseout', () => {
        setHoveredSite(null);
        setHoverPosition(null);
        handlersRef.current.onHoverSite?.(null);
      });
      marker.on('click', () => handlersRef.current.onSelectSite?.(site));
      clusterGroup.addLayer(marker);
      byId.set(site.id, marker);
    });
    markersByIdRef.current = byId;
  }, [sites]);

  // Fit to the visible sites whenever a fresh dataset lands (not on every
  // filter tweak). A map opened on an explicit viewport keeps it for that first
  // fit only — the pending flag is spent on the first fit that has points to
  // work with, so an empty pre-fetch render never burns it.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const points = sites.filter(hasPosition).map(site => [site.latitude, site.longitude]);
    if (points.length === 0) return;
    if (openingFitPendingRef.current) {
      openingFitPendingRef.current = false;
      return;
    }
    map.flyToBounds(L.latLngBounds(points), {
      padding: FIT_PADDING,
      maxZoom: FIT_MAX_ZOOM,
      duration: FLY_DURATION_SECONDS,
    });
    // fitKey drives the fit; `sites` is read but intentionally not a trigger.
  }, [fitKey]);

  // Mirror the hovered sidebar row by lifting its marker (visible markers only;
  // clustered markers have no rendered icon to highlight).
  useEffect(() => {
    const highlighted = [];
    markersByIdRef.current.forEach((marker, siteId) => {
      const on = siteId === hoveredSiteId;
      marker.setZIndexOffset(on ? HIGHLIGHT_Z_OFFSET : 0);
      if (on && marker._icon) {
        marker._icon.classList.add('site-group-marker--hover');
        highlighted.push(marker);
      }
    });
    return () => highlighted.forEach(marker => marker._icon?.classList.remove('site-group-marker--hover'));
  }, [hoveredSiteId]);

  // Ring the selected site so it stays findable among its neighbours.
  useEffect(() => {
    const marker = markersByIdRef.current.get(selectedSiteId);
    const site = sites.find(candidate => candidate.id === selectedSiteId);
    if (!marker || !site) return undefined;
    marker.setIcon(createSelectedSiteIcon(site, marker.options._color));
    marker.setZIndexOffset(SELECTED_Z_OFFSET);
    return () => {
      marker.setIcon(createSiteIcon(site, marker.options._color));
      marker.setZIndexOffset(0);
    };
  }, [selectedSiteId, sites]);

  const containerStyle = useMemo(
    () => ({ position: 'relative', height: '100%', width: '100%', overflow: 'hidden' }),
    [],
  );

  return (
    <div style={containerStyle}>
      <div ref={mapElRef} style={{ height: '100%', width: '100%' }} />
      <MapMarkerPopover site={hoveredSite} position={hoverPosition} isBillboard={isBillboard} hours={popoverHours} />
      {showTerminator && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            zIndex: 1000,
            bgcolor: theme => alpha(theme.palette.background.paper, 0.88),
            borderRadius: 999,
            px: 1.25,
            py: 0.5,
            boxShadow: 1,
            fontSize: 12,
            fontWeight: 600,
            color: 'text.secondary',
            letterSpacing: '0.03em',
          }}
        >
          {t('operationsDashboard.dayNightDivide', 'Gold line marks the day/night divide')}
        </Box>
      )}
    </div>
  );
}

export default forwardRef(SiteMap);
